import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Trash2Icon } from "lucide-react";
import { useState } from "react";
import { Heading } from "react-aria-components";

import { isAdminRole, isSuperadminRole, useViewerRole } from "@/auth/shared";
import { Button } from "@/components/base/button";
import {
  Disclosure,
  DisclosureGroup,
  DisclosureHeader,
  DisclosurePanel,
} from "@/components/base/disclosure";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { queue } from "@/components/base/toast";
import type { Setting, SettingsType } from "@/db/schema/settings";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";

export const Route = createFileRoute("/admin/settings")({
  loader: async ({ context: { queryClient, ...context } }) => {
    const viewer = context.viewer;

    if (!viewer || !isAdminRole(viewer.role)) {
      throw redirect({ to: "/not-found" });
    }
  },
  component: RouteComponent,
});

function formatValue(value: string | number | boolean, type: SettingsType) {
  if (type === "money") {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });

    return formatter.format(value as number);
  }

  if (type === "boolean") {
    return value === true ? "Yes" : "No";
  }

  return value;
}

function RouteComponent() {
  const trpc = useTRPC();
  const { data: settings } = useSuspenseQuery(
    trpc.settings.getAll.queryOptions(),
  );
  const isSuperadmin = isSuperadminRole(useViewerRole());

  return (
    <DefaultLayout
      classNames={{
        content: "flex flex-col space-y-8 max-w-2xl px-3 py-12 mx-auto",
      }}
    >
      <section className="flex flex-col space-y-4">
        <h2
          className={title({
            size: "sm",
            class: "flex flex-row justify-between items-center",
          })}
        >
          <span>Settings</span>
        </h2>

        <DisclosureGroup className="bg-white">
          {settings?.map(({ key, ...setting }) => (
            <Disclosure key={key}>
              <DisclosureHeader
                size="sm"
                contentClassName="flex-1 flex flex-row justify-start items-center gap-4"
              >
                <span className="text-xs">{setting.label}:</span>
                <span className="text-xs font-semibold">
                  {setting.value
                    ? formatValue(setting.value, setting.type)
                    : "not set"}
                </span>
              </DisclosureHeader>
              <DisclosurePanel>
                <UpdateSettingsForm
                  {...setting}
                  settingKey={key}
                  canDelete={isSuperadmin}
                />
              </DisclosurePanel>
            </Disclosure>
          ))}
        </DisclosureGroup>
      </section>
    </DefaultLayout>
  );
}

function UpdateSettingsForm({
  settingKey,
  label,
  value,
  type,
  canDelete,
}: Omit<Setting, "key"> & { settingKey: string; canDelete: boolean }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  function invalidateSettings() {
    queryClient.invalidateQueries(trpc.settings.pathFilter());
  }

  const { mutate } = useMutation({
    ...trpc.settings.update.mutationOptions(),
    onSuccess: () => {
      invalidateSettings();
      queue.add({
        variant: "success",
        title: "Success!",
        description: "Updated setting successfully.",
      });
    },
  });

  const { mutate: deleteSetting, isPending: isDeleting } = useMutation({
    ...trpc.settings.delete.mutationOptions(),
    onSuccess: () => {
      invalidateSettings();
      setConfirmDeleteOpen(false);
      queue.add({
        variant: "success",
        title: "Deleted",
        description: `Setting "${label}" has been deleted.`,
      });
    },
    onError: (error) => {
      queue.add({
        variant: "danger",
        title: "Error",
        description: error.message,
      });
    },
  });

  const isBoolean = type === "boolean";

  const form = useAppForm({
    defaultValues: {
      value,
      booleanValue: value === true,
      unsetValue: value === null,
    },
    listeners: {
      onChange: ({ fieldApi, formApi }) => {
        if (isBoolean && fieldApi.name === "booleanValue") {
          formApi.setFieldValue("value", fieldApi.state.value);
        } else if (fieldApi.name === "unsetValue" && fieldApi.state.value) {
          if (formApi.getFieldValue("value") !== null) {
            formApi.setFieldValue("value", null);
          }
        } else if (fieldApi.name === "unsetValue" && !fieldApi.state.value) {
          if (formApi.getFieldValue("value") !== value) {
            formApi.setFieldValue("value", value);
          }
        } else if (fieldApi.name === "value" && fieldApi.state.value != null) {
          if (formApi.getFieldValue("unsetValue") !== false) {
            formApi.setFieldValue("unsetValue", false);
          }
        }
      },
    },
    onSubmit: ({ value: { value, unsetValue }, formApi }) => {
      if (formApi.state.isDefaultValue) return;

      mutate(
        {
          key: settingKey,
          value: isBoolean
            ? (value ?? false)
            : unsetValue
              ? null
              : (value ?? null),
        },
        {
          onSuccess: () => {
            formApi.reset();
          },
        },
      );
    },
  });

  return (
    <form
      className="flex flex-col gap-y-3"
      onSubmit={(e) => {
        e.preventDefault();

        form.handleSubmit();
      }}
    >
      <h2>Update {label}</h2>

      {isBoolean ? (
        <form.AppField name="booleanValue">
          {(field) => <field.Checkbox label={label} field={field} />}
        </form.AppField>
      ) : (
        <form.Subscribe selector={(state) => state.values.unsetValue}>
          {(unsetValue) => (
            <form.AppField name="value">
              {(field) => (
                <>
                  {["money", "int", "float"].includes(type) ? (
                    <field.Number
                      field={field}
                      aria-label={label}
                      formatOptions={
                        type === "money"
                          ? {
                              style: "currency",
                              currency: "USD",
                            }
                          : undefined
                      }
                      isDisabled={unsetValue}
                    />
                  ) : (
                    <field.Text
                      field={field}
                      aria-label={label}
                      isDisabled={unsetValue}
                    />
                  )}
                </>
              )}
            </form.AppField>
          )}
        </form.Subscribe>
      )}

      {!isBoolean && (
        <form.AppField name="unsetValue">
          {(field) => <field.Checkbox label="Empty value" field={field} />}
        </form.AppField>
      )}

      <form.AppForm>
        <form.Footer>
          {canDelete && (
            <Button
              variant="outline"
              color="danger"
              onPress={() => setConfirmDeleteOpen(true)}
            >
              <Trash2Icon className="size-4" />
              Delete
            </Button>
          )}
          <form.SubmitButton requireChange={false}>Save</form.SubmitButton>
        </form.Footer>
      </form.AppForm>

      {canDelete && (
        <Modal isOpen={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
          <div className="p-6">
            <Heading slot="title" className="text-lg font-semibold">
              Delete Setting
            </Heading>
            <p className="mt-2 text-sm text-gray-600">
              Are you sure you want to delete <strong>{label}</strong>? This
              action cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                onPress={() => setConfirmDeleteOpen(false)}
              >
                Cancel
              </Button>
              <Button
                color="danger"
                isDisabled={isDeleting}
                onPress={() => deleteSetting({ key: settingKey })}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </form>
  );
}
